import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Star, Zap } from 'lucide-react';
import { EV } from '../data/mock';
import Reveal from '../components/Reveal';

export default function EVPage() {
  return (
    <div data-testid="ev-page">
      <section className="px-6 md:px-10 pt-32 md:pt-36 pb-16 md:pb-20">
        <div className="flex items-center justify-between mb-10 fade-up">
          <span className="tag bg-red text-white"><Zap size={12} /> Tesla · EV protection</span>
          <p className="eyebrow hidden md:block text-[#6b6b6b]">Ottawa · XPEL authorized</p>
        </div>
        <h1 className="font-display h-display max-w-[14ch] fade-up" data-testid="ev-title">
          {EV.hero[0]} <span className="text-red">{EV.hero[1]}</span>
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-8">
          <div className="md:col-span-6 md:col-start-7 fade-up fade-up-2">
            <p className="body-lg">{EV.intro}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/build" data-testid="ev-build-btn" className="btn btn-red">Build your EV package <ArrowRight size={14} /></Link>
              <Link to="/#book" className="btn btn-outline">Book a consultation</Link>
            </div>
          </div>
        </div>
      </section>

      <Reveal className="px-6 md:px-10 border-t border-[#e6e2dc]">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {EV.stats.map(([v, l], i) => (
            <div key={l} className={`py-10 md:py-14 ${i < 3 ? 'md:border-r border-[#e6e2dc] md:pr-8' : ''} ${i > 0 ? 'md:pl-8' : ''}`}>
              <p className="font-display text-4xl md:text-5xl leading-none text-red">{v}</p>
              <p className="meta mt-4">{l}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <section className="px-6 md:px-10 py-20 md:py-28 bg-warm">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-5">
            <p className="eyebrow">01 / Why EVs are different</p>
            <h2 className="font-display h-section mt-6">Four problems. <span className="text-red">One sequence.</span></h2>
            <div className="aspect-[4/5] mt-8 overflow-hidden card-hover border border-[#e6e2dc]">
              <img src={EV.image} alt="Protected Tesla at SupremeX" className="w-full h-full object-cover card-media" />
            </div>
          </Reveal>
          <div className="md:col-span-7">
            {EV.problems.map((p, i) => (
              <Reveal key={p.n} delay={i * 70} className="grid grid-cols-12 gap-4 py-8 border-b border-[#e6e2dc]">
                <div className="col-span-2 md:col-span-1 meta text-red pt-1">{p.n}</div>
                <div className="col-span-10 md:col-span-11">
                  <p className="font-display text-2xl md:text-3xl leading-tight">{p.title}</p>
                  <p className="mt-3 body max-w-xl">{p.body}</p>
                  <p className="meta mt-4 text-red">→ {p.fix}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 py-20 md:py-28">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <p className="eyebrow md:col-span-5">02 / EV packages</p>
          <h2 className="font-display h-section md:col-span-7">Pick a starting <span className="text-red">point.</span></h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {EV.packages.map((p, i) => (
            <Reveal key={p.n} delay={i * 90} className="p-7 md:p-8 border border-[#e6e2dc] lift bg-white" data-testid={`ev-package-${i}`}>
              <div className="flex items-center justify-between">
                <p className="meta text-red">{p.n}</p>
                <ArrowUpRight size={16} className="text-[#c9c4bb]" />
              </div>
              <p className="font-display text-3xl md:text-4xl mt-10 leading-tight">{p.title}</p>
              <p className="meta mt-6 normal-case tracking-normal text-[12px] text-[#6b6b6b] leading-relaxed">{p.body}</p>
              <p className="font-display text-2xl mt-8 text-red">{p.est}</p>
              <Link to="/build" className="meta linkline mt-6 inline-block text-red">Customize in the builder</Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-10 py-20 md:py-28 bg-charcoal text-white">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4">
            <div className="flex gap-1">{[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-red text-red" />)}</div>
            <p className="meta mt-4 text-white/60">Tesla Model Y owner · Google</p>
          </div>
          <blockquote className="md:col-span-8 font-display text-2xl md:text-4xl leading-tight text-white">
            &ldquo;{EV.review.text}&rdquo;
            <footer className="meta mt-8 text-red">— {EV.review.name}</footer>
          </blockquote>
        </Reveal>
      </section>
    </div>
  );
}
